/**
 * HoldComposer — the right-hand panel of the "Hold dates" modal.
 *
 * WHAT CHANGED, AND WHY IT IS NOT THE OLD BATCH CARD
 * ==================================================
 * This used to be one card above the calendar that set ONE booking slot and
 * ONE duration for every date in the save. That is not how a hold request
 * actually arrives: a firm asks for the 25th all day or the 22nd afternoon,
 * and a single batch choice forced the whole save down to whatever the most
 * awkward date could take. Pick a fully free day and a half-booked day
 * together and Full Day greyed out for BOTH.
 *
 * So the slot and the duration are now set per date, and this panel shows one
 * date at a time — the one the mediator is currently on. The API already
 * accepted this: `normalizeHoldSelections` reads `duration_amount`,
 * `duration_unit` and the notify flags off each entry in `selections`, falling
 * back to batch-level values. No endpoint changed.
 *
 * WHAT IS STILL BATCH
 * ===================
 * Hold type and the two notification preferences. They describe the whole
 * request rather than a date, so one value carries across every date in the
 * save — change it on the third date and the first date changes with it.
 *
 * They are SHOWN on every date. They were once shown on the first date only,
 * on the reasoning that repeating them implied they were per-date; in practice
 * that reads as the panel losing a section. A mediator who lands on date two
 * and finds no hold type cannot tell whether the setting is missing, hidden or
 * already spent. The "applies to every date" badge on the header is what
 * carries the batch meaning now, and it is visible wherever the controls are
 * rather than only where they happened to be introduced.
 *
 * WHAT THE CALENDAR NO LONGER DOES
 * ================================
 * The per-date chooser popover is gone. Availability is shown inline on the
 * slot buttons for the ACTIVE date — "Full Day / BOOKED" — which the popover
 * could never do across a multi-date selection because it only ever knew about
 * the one day it was anchored to.
 */
import React from 'react'
import {
    BORDER_LIGHT,
    FONT,
    GREEN,
    HAIRLINE,
    INK,
    INK_SOFT,
    MUTED,
    TAN_BORDER,
    TAN_INK,
    TAN_TRACK,
    TRACK_OFF,
    cardStyle,
} from './holdTheme'
import { MAX_DURATION_AMOUNT, formatShortDate, previewFollowUpIso } from './holdUtils'

/* Booking slot. `slots` are the DATABASE values and never change; the labels
 * now match them, so the second half of the day is "Afternoon" here exactly as
 * it is on the schedule board and in the booking widget.
 *
 * Called "Booking slot" and no longer "Portion of day": the underlying values
 * are literally slots (`availability.slot`, `cm_schedule_slots`), so the label
 * now matches the model instead of inventing a second vocabulary for it. */
export const PORTION_OPTIONS = [
    { key: 'fullday', label: 'Full Day', slots: ['morning', 'afternoon'] },
    { key: 'morning', label: 'Morning', slots: ['morning'] },
    { key: 'afternoon', label: 'Afternoon', slots: ['afternoon'] },
]

export const DURATION_PRESETS = [
    { key: '1d', label: '1 day', amount: 1, unit: 'days' },
    { key: '2d', label: '2 days', amount: 2, unit: 'days' },
    { key: '3d', label: '3 days', amount: 3, unit: 'days' },
    { key: '1w', label: '1 week', amount: 1, unit: 'weeks' },
    { key: 'custom', label: 'Custom', amount: null, unit: null },
]

/* ===========================================================================
 * THE CLIENT-SIDE GATES. One per email. Both are live now, and they stay
 * separate because they became live for different reasons and could stop being
 * live independently.
 *
 * CONFIRMATION — placeHolds calls sendHoldConfirmation once the holds are
 * committed, so the firm is told which dates are being held for them.
 *
 * EXPIRATION — one switch over two behaviours, which is the whole point of it.
 * ON, the hold is a timer: cron/holds/holdExpirySweep runs hourly, and when a
 * date's duration is up the date is released AND the firm is emailed. OFF, the
 * hold is a standing reservation: no email, no release, and the date stays held
 * until the mediator lets it go by hand.
 *
 * Do not split those two effects apart again. An earlier build released
 * regardless and treated this as "tell them or don't" — which quietly took a
 * date off a mediator who had asked to be the one who ends it.
 *
 * Both default ON. Holding dates a firm never hears about is a note to self,
 * and releasing dates without saying so is worse.
 *
 * Neither constant decides WHO is emailed. The backend re-checks the
 * per-mediator opt-in (booking_features.hold_emails) on every send and skips
 * silently without it; these only decide what this screen may offer and
 * promise.
 * =========================================================================== */
export const HOLD_CONFIRMATION_ENABLED = true
export const HOLD_EXPIRATION_ENABLED = true

/** Batch-level defaults. Per-date values live on each selection entry. */
export const HOLD_COMPOSER_DEFAULTS = {
    notifyConfirmation: HOLD_CONFIRMATION_ENABLED,
    notifyExpiration: HOLD_EXPIRATION_ENABLED,
}

/** What a newly picked date starts as. Duration defaults to a week because the
 *  follow-up sentence has to say something the moment the date is chosen. */
export const NEW_SELECTION_DEFAULTS = {
    preset: '1w',
    customAmount: 5,
    customUnit: 'weeks',
}

/**
 * The notification preferences a save may actually carry. Enforced here rather
 * than trusted from component state, so there is exactly one place that can put
 * a `true` on the wire and a closed gate cannot be routed around by a stale
 * piece of state.
 *
 * `hold_notify_on_expiration` is the sweep's own selector, which is why it goes
 * through the gate rather than straight from component state: while the sweep
 * was unscheduled, a `true` written here would have sat in the table building
 * the backlog that the first scheduled run then mailed out. The sweep exists
 * now, and its window means only holds coming due in the last few days are ever
 * chased — but the gate stays, because turning the sweep back off must be one
 * constant and not an archaeology exercise.
 */
export function resolveHoldNotifications(value) {
    const v = { ...HOLD_COMPOSER_DEFAULTS, ...(value || {}) }
    return {
        confirmation: HOLD_CONFIRMATION_ENABLED && v.notifyConfirmation === true,
        expiration: HOLD_EXPIRATION_ENABLED && v.notifyExpiration === true,
    }
}

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi)

/** The slot values a chosen booking slot maps to. */
export function portionSlots(portionKey) {
    return (PORTION_OPTIONS.find((p) => p.key === portionKey) || PORTION_OPTIONS[0]).slots
}

/**
 * The { amount, unit } one selection is asking for, clamped to what the
 * backend accepts. Returns nulls only for an unrecognised preset, in which
 * case the save carries no structured duration rather than half a pair (which
 * the API rejects).
 */
export function resolveHoldDuration(value) {
    const v = value || NEW_SELECTION_DEFAULTS
    if (v.preset === 'custom') {
        const unit = v.customUnit === 'weeks' ? 'weeks' : 'days'
        const raw = Math.round(Number(v.customAmount))
        const amount = Number.isFinite(raw) ? clamp(raw, 1, MAX_DURATION_AMOUNT[unit]) : 1
        return { amount, unit }
    }
    const preset = DURATION_PRESETS.find((p) => p.key === v.preset)
    return preset && preset.amount ? { amount: preset.amount, unit: preset.unit } : { amount: null, unit: null }
}

/** A selection is finished when it has both a slot and a duration. */
export function isSelectionComplete(sel) {
    return !!(sel && sel.portion && sel.preset)
}

/* ---------- primitives ---------- */

const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: '#A3A39B',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    marginBottom: 9,
}

function StepDot({ n, done }) {
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 17,
                height: 17,
                borderRadius: '50%',
                background: done ? GREEN : '#D9D8D2',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0,
                flexShrink: 0,
            }}
        >
            {n}
        </span>
    )
}

/* A choice button. Selected reads amber for a booking slot (amber means HELD
 * everywhere in this product) and green for a duration (green means a choice
 * you made). Amber is the ring-on-light treatment the calendar already uses
 * for a held day, not a solid fill — #E3C489 is the BORDER of its family. */
function Choice({ label, note, on, tone = 'amber', disabled, onClick }) {
    const base = {
        border: `1px solid ${BORDER_LIGHT}`,
        background: '#fff',
        borderRadius: 10,
        padding: '12px 6px',
        font: 'inherit',
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 600,
        color: INK_SOFT,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color .13s, background .13s, color .13s',
        width: '100%',
    }
    const selected =
        tone === 'green'
            ? { background: GREEN, borderColor: GREEN, color: '#fff', fontWeight: 700 }
            : {
                  background: TAN_TRACK,
                  borderColor: TAN_BORDER,
                  color: TAN_INK,
                  fontWeight: 700,
                  boxShadow: `inset 0 0 0 1px ${TAN_BORDER}`,
              }
    const off = { background: '#F7F7F5', borderColor: '#EDECE8', color: '#BCBBB4' }
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            style={{ ...base, ...(on ? selected : {}), ...(disabled ? off : {}) }}
        >
            {label}
            {note ? (
                <span
                    style={{
                        display: 'block',
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        marginTop: 2,
                        color: '#BCBBB4',
                    }}
                >
                    {note}
                </span>
            ) : null}
        </button>
    )
}

function StepButton({ sign, onClick, disabled, label }) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            disabled={disabled}
            style={{
                width: 32,
                height: 36,
                border: 0,
                background: '#fff',
                fontSize: 16,
                color: INK_SOFT,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
            }}
        >
            {sign}
        </button>
    )
}

function Toggle({ checked, onChange, disabled, label }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            style={{
                width: 38,
                height: 22,
                borderRadius: 999,
                background: checked ? GREEN : TRACK_OFF,
                border: 0,
                position: 'relative',
                flexShrink: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
            }}
        >
            <b
                style={{
                    position: 'absolute',
                    top: 2.5,
                    left: 2.5,
                    width: 17,
                    height: 17,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,.22)',
                    transform: checked ? 'translateX(16px)' : 'none',
                    transition: 'transform .16s',
                }}
            />
        </button>
    )
}

/**
 * A dropdown the app draws itself. A native <select> renders its menu outside
 * the page's stacking context in the operating system's own colours, which
 * reads as a browser control dropped into the middle of the dialog. It also
 * cannot be styled to match the choice buttons beside it.
 */
function Dropdown({ value, options, onChange, disabled, width }) {
    const [open, setOpen] = React.useState(false)
    const ref = React.useRef(null)
    React.useEffect(() => {
        if (!open) return undefined
        const away = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        const esc = (e) => e.key === 'Escape' && setOpen(false)
        document.addEventListener('mousedown', away)
        document.addEventListener('keydown', esc)
        return () => {
            document.removeEventListener('mousedown', away)
            document.removeEventListener('keydown', esc)
        }
    }, [open])
    return (
        <div ref={ref} style={{ position: 'relative', width: width || '100%', flexShrink: 0 }}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    width: '100%',
                    border: `1px solid ${open ? GREEN : BORDER_LIGHT}`,
                    boxShadow: open ? `0 0 0 3px rgba(20,83,45,.10)` : 'none',
                    background: '#fff',
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontFamily: FONT,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: INK,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    textAlign: 'left',
                }}
            >
                {options.find((o) => o.value === value)?.label || value}
                <span style={{ color: MUTED, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>
                    ⌄
                </span>
            </button>
            {open ? (
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 'calc(100% + 6px)',
                        zIndex: 30,
                        background: '#fff',
                        border: `1px solid ${HAIRLINE}`,
                        borderRadius: 12,
                        padding: 5,
                        boxShadow: '0 12px 30px rgba(16,24,40,.16)',
                    }}
                >
                    {options.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => {
                                onChange(o.value)
                                setOpen(false)
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 9,
                                width: '100%',
                                border: 0,
                                background: 'none',
                                fontFamily: FONT,
                                fontSize: 13.5,
                                padding: '9px 10px',
                                borderRadius: 8,
                                cursor: 'pointer',
                                color: o.value === value ? GREEN : INK_SOFT,
                                fontWeight: o.value === value ? 600 : 400,
                                textAlign: 'left',
                            }}
                        >
                            <span style={{ width: 13, color: GREEN }}>{o.value === value ? '✓' : ''}</span>
                            {o.label}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    )
}

/* ---------- the panel ---------- */

/**
 * @param selection   the date being configured: { date, portion, preset, customAmount, customUnit }
 * @param dayMeta     getHoldCalendar payload, for per-date slot availability
 * @param onChange    (patch) => void, merged into the active selection
 * @param batch       { notifyConfirmation, notifyExpiration }
 * @param holdType    batch hold type value — one value for the whole save,
 *                    editable from whichever date is on screen
 * @param inquiryContact the inquiry's contact_email, named in the notifications
 *                    copy so the mediator can see WHO is about to be emailed
 *                    before they save, not after
 */
export default function HoldComposer({
    selection,
    dayMeta,
    onChange,
    batch,
    onBatchChange,
    holdType,
    onHoldTypeChange,
    holdTypeOptions,
    inquiryContact = '',
    batchReady = false,
    disabled = false,
}) {
    if (!selection) return null

    const iso = selection.date

    /* Availability for THIS date only. The old batch model intersected every
     * selected date, so one half-booked day switched Full Day off for all of
     * them. */
    /* `unavailable` is a real state and must be listed here. Anything this
       function does not recognise collapses to 'none', which is also not
       holdable, so an unknown state fails closed — but it would have shown the
       wrong reason, and "no availability row" is a different problem from "the
       mediator does not work that half". */
    const slotState = (slot) => {
        if (!dayMeta) return 'holdable'
        const d = dayMeta[iso]
        if (!d) return 'none'
        const st = d.slots?.[slot]?.state
        return st === 'booked' || st === 'held' || st === 'holdable' || st === 'unavailable'
            ? st
            : 'none'
    }
    const canTake = (p) => p.slots.every((s) => slotState(s) === 'holdable')
    const whyNot = (p) => {
        const blocked = p.slots.filter((s) => slotState(s) !== 'holdable')
        if (!blocked.length) return null
        const st = slotState(blocked[0])
        if (st === 'booked') return 'Booked'
        if (st === 'held') return 'On hold'
        // Two different problems, and the mediator fixes them in the same
        // place but for different reasons: 'unavailable' is a half they have
        // closed, 'none' is a date their schedule does not cover at all.
        if (st === 'unavailable') return 'Not open'
        return 'No availability'
    }

    /* One computed answer for "what will actually reach the wire", used by every
       control and every sentence in this panel. The resolver is the same one
       handlePlaceHolds calls, so the screen and the save cannot disagree. */
    const gated = resolveHoldNotifications(batch)

    const dur = resolveHoldDuration(selection)
    const followUpIso = dur.amount ? previewFollowUpIso(dur.amount, dur.unit) : null
    const followUpDays = dur.amount ? (dur.unit === 'weeks' ? dur.amount * 7 : dur.amount) : null

    const unavailable = PORTION_OPTIONS.filter((p) => !canTake(p))
    /* Say WHY each half is off, not just that it is. "already taken" was
       written when the only way to lose a half was a booking; a half the
       mediator never opened is not taken by anyone, and telling them it is
       sends them hunting for a case that does not exist. */
    const REASON_WORD = {
        booked: 'is booked',
        held: 'is on hold',
        unavailable: 'is not open on your schedule',
        none: 'has no availability',
    }
    const blockedBits = ['morning', 'afternoon']
        .filter((s) => slotState(s) !== 'holdable')
        .map((s) => `${s} ${REASON_WORD[slotState(s)] || 'is unavailable'}`)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22, fontFamily: FONT }}>
            {/* 2 — booking slot */}
            <section>
                <div style={labelStyle}>
                    <StepDot n={2} done={!!selection.portion} /> Booking slot
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                    {PORTION_OPTIONS.map((p) => (
                        <Choice
                            key={p.key}
                            label={p.label}
                            note={whyNot(p)}
                            on={selection.portion === p.key}
                            disabled={disabled || !canTake(p)}
                            onClick={() => onChange({ portion: selection.portion === p.key ? null : p.key })}
                        />
                    ))}
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: MUTED }}>
                    {unavailable.length
                        ? `${formatShortDate(iso)}: ${blockedBits.join(' and ')} — those options are off.`
                        : `${formatShortDate(iso)} is completely open.`}
                </div>
            </section>

            {/* 3 — requested for, revealed once a slot is chosen */}
            {selection.portion ? (
                <section>
                    <div style={labelStyle}>
                        <StepDot n={3} done={!!selection.preset} /> Requested for
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 7 }}>
                        {DURATION_PRESETS.map((p) => (
                            <Choice
                                key={p.key}
                                label={p.label}
                                tone="green"
                                on={selection.preset === p.key}
                                disabled={disabled}
                                onClick={() => onChange({ preset: selection.preset === p.key ? null : p.key })}
                            />
                        ))}
                    </div>

                    {selection.preset === 'custom' ? (
                        <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 9 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: `1px solid ${BORDER_LIGHT}`,
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                    width: 'max-content',
                                }}
                            >
                                <StepButton
                                    sign="−"
                                    label="Fewer"
                                    disabled={disabled || selection.customAmount <= 1}
                                    onClick={() => onChange({ customAmount: Math.max(1, selection.customAmount - 1) })}
                                />
                                <span
                                    style={{
                                        minWidth: 40,
                                        textAlign: 'center',
                                        fontSize: 13.5,
                                        fontWeight: 600,
                                        lineHeight: '36px',
                                        borderLeft: `1px solid ${BORDER_LIGHT}`,
                                        borderRight: `1px solid ${BORDER_LIGHT}`,
                                    }}
                                >
                                    {selection.customAmount}
                                </span>
                                <StepButton
                                    sign="+"
                                    label="More"
                                    disabled={
                                        disabled ||
                                        selection.customAmount >= MAX_DURATION_AMOUNT[selection.customUnit]
                                    }
                                    onClick={() =>
                                        onChange({
                                            customAmount: Math.min(
                                                MAX_DURATION_AMOUNT[selection.customUnit],
                                                selection.customAmount + 1
                                            ),
                                        })
                                    }
                                />
                            </div>
                            <Dropdown
                                width={132}
                                value={selection.customUnit}
                                disabled={disabled}
                                options={[
                                    { value: 'days', label: 'days' },
                                    { value: 'weeks', label: 'weeks' },
                                ]}
                                onChange={(unit) =>
                                    onChange({
                                        customUnit: unit,
                                        customAmount: Math.min(selection.customAmount, MAX_DURATION_AMOUNT[unit]),
                                    })
                                }
                            />
                        </div>
                    ) : null}

                    {followUpIso ? (
                        <div style={{ marginTop: 10, fontSize: 12, color: INK_SOFT }}>
                            {/* What this date DOES, which depends on the expiration
                                 switch below — so the line has to read the switch
                                 rather than assert one behaviour. Off, the date is
                                 the end of the intended hold and nothing acts on it;
                                 on, it is when the system takes the date back. A
                                 mediator picking a duration is entitled to know
                                 which, before saving rather than after. */}
                            {/* Through the gate, like every other consumer. Reading
                                 batch.notifyExpiration directly meant that turning
                                 HOLD_EXPIRATION_ENABLED off would leave the toggle
                                 correctly off and disabled, write nothing to the wire,
                                 and still promise "Releases automatically on Sep 11"
                                 here — the one place that routed around the
                                 one-place-decides rule this file sets out above. */}
                            {gated.expiration ? 'Releases automatically on ' : 'Held through '}
                            <b style={{ color: INK, fontWeight: 600 }}>{formatShortDate(followUpIso)}</b>{' '}
                            <span style={{ color: MUTED }}>
                                {followUpDays} days out{gated.expiration ? '' : ' · you release it'}
                            </span>
                        </div>
                    ) : null}
                </section>
            ) : null}

            {/* 4 — batch settings. One value for the whole request, shown on
                every date so the panel never loses a section as you move
                between them. The header badge says which it is; editing here
                edits the save, not this date. */}
            {/* Keyed on the BATCH, not the active date. These settings belong to
                the whole save, so tying their visibility to whichever date
                happens to be focused made them flash in and out as focus
                auto-advanced — appearing when a date completed, vanishing
                550ms later when focus moved to the next unset one, and only
                settling on the last date. A mediator watching that reasonably
                concludes the toggles belong to the final date, which is the
                per-date misreading the "applies to every date" badge exists to
                prevent. */}
            {batchReady || isSelectionComplete(selection) ? (
                <section>
                    <div style={labelStyle}>
                        <StepDot n={4} done /> How it behaves
                        <span
                            style={{
                                marginLeft: 'auto',
                                textTransform: 'none',
                                letterSpacing: 0,
                                fontWeight: 500,
                                fontSize: 11,
                                color: MUTED,
                            }}
                        >
                            applies to every date
                        </span>
                    </div>
                    <div style={{ ...cardStyle, background: '#FCFCFB', padding: '13px 15px', boxShadow: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Hold type</h4>
                                <p style={{ margin: '2px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.4 }}>
                                    Soft holds can be overridden by a confirmed booking.
                                </p>
                            </div>
                            <Dropdown
                                width={186}
                                value={holdType}
                                disabled={disabled}
                                options={holdTypeOptions}
                                onChange={onHoldTypeChange}
                            />
                        </div>

                        <div
                            style={{
                                marginTop: 11,
                                paddingTop: 11,
                                borderTop: `1px solid #F1F0EC`,
                            }}
                        >
                            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Notifications</h4>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.4 }}>
                                Sent to the inquiry contact — {inquiryContact || 'the firm on this inquiry'}.
                            </p>
                        </div>

                        {/* Both emails, side by side again. The follow-up spent
                            a while as a disabled switch wearing a "not yet
                            enabled" badge, then came out entirely — an apology
                            occupying half the row is not worth the space. It is
                            back because it now does something: a daily sweep
                            sends it on the follow-up date. */}
                        <div
                            style={{
                                marginTop: 11,
                                paddingTop: 11,
                                borderTop: `1px solid #F1F0EC`,
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 14,
                            }}
                        >
                            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                                <Toggle
                                    checked={gated.confirmation}
                                    disabled={!HOLD_CONFIRMATION_ENABLED || disabled}
                                    label="On-hold confirmation"
                                    onChange={(v) => onBatchChange({ notifyConfirmation: v })}
                                />
                                <div>
                                    <h4 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>On-hold confirmation</h4>
                                    <p style={{ margin: '2px 0 0', fontSize: 11.5, color: MUTED }}>
                                        Emails the firm which dates you are holding, as soon as you save.
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                                <Toggle
                                    checked={gated.expiration}
                                    disabled={!HOLD_EXPIRATION_ENABLED || disabled}
                                    label="Expiration notice"
                                    onChange={(v) => onBatchChange({ notifyExpiration: v })}
                                />
                                <div>
                                    {/* "Expiration notice" is the client's name for this and
                                        the name it goes back to. It was briefly relabelled
                                        "Follow-up notice" on the reasoning that nothing
                                        actually expires — a hold sits until someone clears
                                        it — but renaming a control the client named is not
                                        a call to make silently. The caveat belongs in the
                                        description, where it is doing the same job without
                                        arguing with the label. */}
                                    <h4 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Expiration notice</h4>
                                    <p style={{ margin: '2px 0 0', fontSize: 11.5, color: MUTED }}>
                                        {/* Both halves of what the switch does, in the order
                                            they happen, and what OFF means — which is the
                                            half a mediator would otherwise have to discover.
                                            Off is not "stays held quietly"; it is "stays held
                                            until you say otherwise", and that is a promise
                                            worth making explicitly. */}
                                        On: each date is released as its own time runs out, and the
                                        firm is emailed. Off: nothing is sent and the dates stay
                                        held until you release them.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}
        </div>
    )
}
